import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match, RouteComponentProps } from 'react-router-dom';

// Components
import App from '../../../../components/app';
import ErrorMessage from '../../../../components/error-message';
import EnhancedForm from '../../../../components/form';
import EnhancedListItem from '../../../../components/listItem';
// Modules
import { Extra, ExtraType, TParams } from '../../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../../redux/reducers/errors';
import {
    actions as pricingActions,
    selectors as pricingSelectors,
    types as pricingTypes,
} from '../../../../redux/reducers/pricing';
import { selectors as pendingSelectors } from '../../../../redux/reducers/pendingOperations';
// Ressources
import withGlobalStyles from '../../../../utils/global-styles';
import ObjectUtils from '../../../../utils/objectUtils';
import UserUtils, { AccountRight, RightRank } from '../../../../utils/userUtils';
import { FormatType, FormDatas, ListCell } from '../../../../local-types';
import { paths } from '../../../paths';
import { ActionLogs } from '../../../../components/action-logs';

const queryString = require('query-string');

interface PricingFeesAddProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    extras: Extra[];
    extra_types: ExtraType[];
    getFeesAddError: ErrorPayload;
    isSavingFee: boolean;
    getExtra: (id: string) => void;
    createExtra: (payload: any) => void;
    editExtra: (id: string, payload: any) => void;
    getExtraTypes: () => void;
}

interface PricingFeesAddState {
    formData: any;
}

class PricingFeesAddPage extends React.Component<PricingFeesAddProps, PricingFeesAddState> {
    queryParams: any;

    isAdmin: boolean;

    state = {
        formData: {},
    };

    constructor(props) {
        super(props);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.PRICING_MGMT, mode: RightRank.W });
    }

    componentDidMount() {
        const { getExtra, getExtraTypes } = this.props;
        const { id } = queryString.parse(this.props.location.search);
        if (id) {
            getExtra(id);
        }
        getExtraTypes();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.match.params.id !== (this.props.match.params as any).id) {
            const id = this.props.match.params.id;
            this.props.getExtra(id);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const { id } = queryString.parse(props.location.search);
        const { extras } = props;

        const needUpdate = !state.formData.id || state.formData.id !== id;
        if (extras && needUpdate) {
            if (extras[id]) {
                const datas = ObjectUtils.deepCopy(extras[id]);
                datas.percent = parseFloat((datas.percent * 100).toFixed(2));
                if (datas.driver_com) {
                    datas.driver_com = parseFloat((datas.driver_com * 100).toFixed(2));
                }
                if (datas.driver_com_add) {
                    datas.driver_com_add = parseFloat((datas.driver_com_add * 100).toFixed(2));
                }
                return { formData: datas };
            }
        }
        return null;
    }

    generatelistItem = (payload: Extra): ListCell[] => {
        const { t, extra_types: extraTypes } = this.props;
        const extra = extraTypes && extraTypes.find((e) => e.id === payload.type_id);
        return [
            { title: t('common:form.lastname'), data: payload.name },
            { title: t('fees.type'), data: extra && extra.name },
            { title: t('fees.amount'), data: payload.amount },
            { title: t('fees.percent'), data: payload.percent },
            { title: t('fees.global'), data: payload.is_global, type: FormatType.Boolean },
            { title: t('fees.driver_com_add'), data: payload.driver_com_add },
            { title: t('fees.driver_com'), data: payload.driver_com ?? t('fees.driver_com_default') },
            { title: t('fees.driver_amount'), data: payload.driver_amount ?? t('fees.driver_amount_default') },
        ];
    };

    generateInputs = (): FormDatas[] => {
        const { t, extra_types: extraTypes } = this.props;

        const inputs: FormDatas[] = [
            {
                title: t('fees.rider'),
                datas: [
                    { id: 'name', label: t('common:form.lastname'), required: true },
                    {
                        id: 'type_id',
                        label: t('fees.type'),
                        type: 'picker',
                        required: true,
                    },
                    {
                        id: 'amount',
                        label: t('fees.amount'),
                        inputsAttributs: { step: 0.01 },
                        numberNegative: true,
                        type: 'number',
                        required: true,
                    },
                    {
                        id: 'percent',
                        label: t('fees.percent'),
                        inputsAttributs: { step: 0.01, pattern: '[0-9]' },
                        numberNegative: true,
                        type: 'number',
                        required: true,
                    },
                    { id: 'is_global', label: t('fees.global'), type: 'boolean' },
                    {
                        id: 'driver_com_add',
                        label: t('fees.driver_com_add'),
                        inputsAttributs: { step: 0.01 },
                        numberNegative: true,
                        type: 'number',
                    },
                ],
            },
            {
                title: t('fees.driver'),
                datas: [
                    {
                        id: 'driver_com',
                        label: t('fees.driver_com'),
                        inputsAttributs: { step: 0.01 },
                        hint: t('fees.driver_com_hint'),
                        type: 'number',
                    },
                    {
                        id: 'driver_amount',
                        label: t('fees.driver_amount'),
                        hint: t('fees.driver_amount_hint'),
                        inputsAttributs: { step: 0.01 },
                        numberNegative: true,
                        type: 'number',
                    },
                ],
            },
        ];

        if (extraTypes?.length > 0) {
            inputs[0].datas.find((d) => d.id === 'type_id').options = extraTypes.map((r) => {
                return { id: r.id, name: r.name };
            });
        }

        return inputs;
    };

    onSubmitFee = (formData: any, event) => {
        const { id } = queryString.parse(this.props.location.search);
        const { editExtra, createExtra } = this.props;

        // let data = new FormData(event.target);
        this.setState({
            formData: formData,
        });

        const datas = ObjectUtils.deepCopy(formData);
        datas.percent = datas.percent / 100;
        if (datas.driver_com > 0) {
            datas.driver_com = datas.driver_com / 100;
        }
        if (datas.driver_com_add !== 0) {
            datas.driver_com_add = datas.driver_com_add / 100;
        }

        if (id) {
            editExtra(id, datas);
        } else {
            createExtra(datas);
        }
    };

    render() {
        const { t, globalClasses, extras, getFeesAddError, isSavingFee } = this.props;

        const { id } = queryString.parse(this.props.location.search);
        const payload = extras[id] || null;
        const formPayload = this.state.formData;

        let dataLoaded: any = true;
        if (id) {
            dataLoaded = payload;
        }

        return (
            <App
                needRights={{ name: AccountRight.PRICING_MGMT, mode: RightRank.W }}
                header={{
                    title: t(id ? 'fees.edit_fee' : 'fees.new_fee'),
                }}
            >
                <ErrorMessage payload={getFeesAddError} />
                {dataLoaded && extras && formPayload && (
                    <>
                        {this.isAdmin ? (
                            <EnhancedForm
                                payload={formPayload}
                                inputs={this.generateInputs()}
                                backRoute={paths.pricingManageFees}
                                onSubmit={this.onSubmitFee}
                                loader={isSavingFee}
                            />
                        ) : (
                            <EnhancedListItem
                                contentProps={{ style: { flex: 1 } }}
                                datas={this.generatelistItem(formPayload as Extra)}
                                title={t('common:form.general_infos')}
                                lock
                            />
                        )}
                        <p className={globalClasses.block} style={{ whiteSpace: 'pre-wrap' }}>
                            {t('fees.tips')}
                        </p>
                        <ActionLogs history={payload?.history} withValues allValues />
                    </>
                )}
            </App>
        );
    }
}

const PricingFeesAddPageWithTranslation = withGlobalStyles(withTranslation(['pricing', 'common'])(PricingFeesAddPage));

const mapStateToProps = (state) => ({
    extras: pricingSelectors.extras(state),
    extra_types: pricingSelectors.extra_types(state),
    getFeesAddError: errorSelectors.getFeesAddError(state),
    isSavingFee: pendingSelectors.isPending(state, pricingTypes.CREATE_EXTRA, pricingTypes.EDIT_EXTRA),
});

const mapDispatchToProps = {
    getExtra: pricingActions.getExtra,
    createExtra: pricingActions.createExtra,
    editExtra: pricingActions.editExtra,
    getExtraTypes: pricingActions.getExtraTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingFeesAddPageWithTranslation);
